import React, { useState } from "react";
import { Plus, Pencil, X, Users, CheckSquare, ChevronRight } from "lucide-react";
import { toast } from "../ui/Toast";
import { useConfirmDelete } from "../../hooks/useConfirmDelete";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  "Planning":    "text-white/40 bg-white/5 border-white/10",
  "Active":      "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  "On Hold":     "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  "Completed":   "text-blue-400 bg-blue-500/10 border-blue-500/20",
  "Cancelled":   "text-red-400/50 bg-red-500/5 border-red-500/10",
};

const EMPTY = {
  name: "", description: "", status: "Planning",
  start_date: "", end_date: "", owner_id: "",
  member_ids: [], perf_weight: 10,
};

export default function ProjectsPanel({ projects, tasks, members, onCreate, onUpdate, onDelete, onSelectProject }) {
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [draft,    setDraft]    = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  const { confirmDelete, ConfirmDialog } = useConfirmDelete();

  const startNew  = () => { setDraft(EMPTY); setEditing(null); setShowForm(true); };
  const startEdit = (p) => { setDraft({ ...p, member_ids: p.member_ids || [] }); setEditing(p.id); setShowForm(true); };

  const save = async () => {
    if (!draft.name) return;
    setSaving(true); setError(null);
    try {
      if (editing) await onUpdate(editing, draft);
      else await onCreate(draft);
      setShowForm(false); setDraft(EMPTY); setEditing(null);
      toast.success("Project saved");
    } catch (e) { setError(e.message); toast.error("Failed to save project"); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    confirmDelete("this project", async () => {
      try {
        await onDelete(id);
        toast.success("Project deleted");
      } catch (e) {
        toast.error("Failed to delete project");
      }
    });
  };

  const toggleMember = (id) => setDraft(d => ({
    ...d,
    member_ids: d.member_ids.includes(id) ? d.member_ids.filter(x => x !== id) : [...d.member_ids, id],
  }));

  const projectTasks = (pid) => tasks.filter(t => t.project_id === pid);
  const completionPct = (pid) => {
    const pt = projectTasks(pid);
    if (!pt.length) return 0;
    return Math.round((pt.filter(t => t.status === "Done").length / pt.length) * 100);
  };

  return (
    <div className="space-y-5">

      {/* Create / Edit form */}
      {showForm && (
        <div className={`${card} p-5 space-y-4`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {editing ? "Edit Project" : "New Project"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>Project Name</label>
              <input className={inputCls} value={draft.name} onChange={e => setDraft(d => ({...d, name: e.target.value}))} placeholder="e.g. KweryAI v2 Launch" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Description</label>
              <textarea rows={2} className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none"
                value={draft.description || ""} onChange={e => setDraft(d => ({...d, description: e.target.value}))} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={draft.status} onChange={e => setDraft(d => ({...d, status: e.target.value}))}>
                {Object.keys(STATUS_STYLE).map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Owner</label>
              <select className={inputCls} value={draft.owner_id || ""} onChange={e => setDraft(d => ({...d, owner_id: e.target.value}))}>
                <option value="">Select owner…</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Start Date</label>
              <input type="date" className={inputCls} value={draft.start_date || ""} onChange={e => setDraft(d => ({...d, start_date: e.target.value}))} />
            </div>
            <div>
              <label className={labelCls}>End Date</label>
              <input type="date" className={inputCls} value={draft.end_date || ""} onChange={e => setDraft(d => ({...d, end_date: e.target.value}))} />
            </div>
            <div>
              <label className={labelCls}>Performance Weight %</label>
              <input type="number" min="0" max="100" className={inputCls} value={draft.perf_weight ?? 10}
                onChange={e => setDraft(d => ({...d, perf_weight: Number(e.target.value)}))} />
              <p className="text-xs text-white/20 mt-1">How much this project counts toward perf score</p>
            </div>
          </div>

          {/* Member assignment */}
          <div>
            <label className={labelCls}>Assign Members</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
              {members.map(m => {
                const active = (draft.member_ids || []).includes(m.id);
                return (
                  <button key={m.id} onClick={() => toggleMember(m.id)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-all text-left ${
                      active ? "border-[#00ff90]/40 bg-[#00ff90]/8 text-[#00ff90]" : "border-white/5 bg-white/3 text-white/50 hover:border-white/15"
                    }`}>
                    <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[10px] shrink-0">
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                    <span className="truncate">{m.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {error && <p className="text-red-400 text-xs">⚠ {error}</p>}
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => setShowForm(false)} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving || !draft.name}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : editing ? "Update Project" : "Create Project"}
            </button>
          </div>
        </div>
      )}

      {/* Projects list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Projects</span>
          <button onClick={startNew}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> New Project
          </button>
        </div>

        <div className="divide-y divide-white/5">
          {projects.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No projects yet. Create one to get started.</p>
          ) : projects.map(p => {
            const pct    = completionPct(p.id);
            const ptasks = projectTasks(p.id);
            const statusStyle = STATUS_STYLE[p.status] || STATUS_STYLE.Planning;
            const owner  = members.find(m => m.id === p.owner_id);
            const pMembers = members.filter(m => (p.member_ids || []).includes(m.id));
            return (
              <div key={p.id} className="px-5 py-4 hover:bg-white/3 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm text-white/80 font-semibold">{p.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>{p.status}</span>
                      {p.perf_weight > 0 && (
                        <span className="text-xs text-[#00ff90]/40 border border-[#00ff90]/15 px-2 py-0.5 rounded-full">
                          {p.perf_weight}% perf weight
                        </span>
                      )}
                    </div>
                    {p.description && <p className="text-xs text-white/30 mb-2">{p.description}</p>}

                    <div className="flex items-center gap-4 flex-wrap text-xs text-white/30">
                      {owner     && <span>Owner: <span className="text-white/50">{owner.name}</span></span>}
                      {p.end_date && <span>Due: {p.end_date}</span>}
                      <span><CheckSquare size={11} className="inline mr-1" />{ptasks.filter(t => t.status === "Done").length}/{ptasks.length} tasks</span>
                    </div>

                    {/* Members */}
                    {pMembers.length > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        {pMembers.slice(0, 6).map(m => (
                          <div key={m.id} title={m.name}
                            className="w-6 h-6 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[10px] text-white/50">
                            {m.name?.[0]?.toUpperCase()}
                          </div>
                        ))}
                        {pMembers.length > 6 && <span className="text-xs text-white/25">+{pMembers.length - 6}</span>}
                      </div>
                    )}

                    {/* Progress bar */}
                    {ptasks.length > 0 && (
                      <div className="flex items-center gap-2 mt-3">
                        <div className="flex-1 h-1.5 rounded-full bg-white/5">
                          <div className="h-full rounded-full bg-[#00ff90] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-[#00ff90]/50 w-8 text-right">{pct}%</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => onSelectProject(p.id)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors flex items-center gap-1">
                      Tasks <ChevronRight size={12} />
                    </button>
                    <button onClick={() => startEdit(p)}
                      className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => handleDelete(p.id)}
                      className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {ConfirmDialog}
    </div>
  );
}